import type { QueryClient } from '@tanstack/react-query';
import { storyboardVideosKeys } from '@/hooks/api/useStoryboardVideos';
import type { StoryboardVideoResponse } from '@/services/api/storyboard-resources';
import { useCollaborationHighlightStore } from '@/stores/collaborationHighlightStore';

function collectStoryboardVideoRefreshProjectIds(payload: {
  projectId?: string | null;
  parentProjectId?: string | null;
  targetProjectId?: string | null;
  affectedProjectIds?: readonly string[];
}): string[] {
  return [
    ...new Set(
      [
        payload.projectId,
        payload.parentProjectId,
        payload.targetProjectId,
        ...(payload.affectedProjectIds ?? []),
      ].filter((value): value is string => Boolean(value?.trim()))
    ),
  ];
}

function isStoryboardBoardQueryForProject(
  queryKey: readonly unknown[],
  projectId: string
): boolean {
  if (queryKey[0] !== 'storyboardBoard') return false;
  const queryType = queryKey[1];
  if (queryType !== 'list' && queryType !== 'skeleton' && queryType !== 'paginated') {
    return false;
  }
  return queryKey[2] === projectId;
}

function refetchActiveStoryboardBoardQueries(
  queryClient: QueryClient,
  projectIds: readonly string[]
): Promise<unknown> {
  if (projectIds.length === 0) return Promise.resolve();

  const projectIdSet = new Set(projectIds);
  return queryClient.refetchQueries({
    predicate: (query) =>
      Array.from(projectIdSet).some((projectId) =>
        isStoryboardBoardQueryForProject(query.queryKey, projectId)
      ),
    type: 'active',
  });
}

/** 协作者收到视频新增/更新/删除事件后，强制拉齐视频列表与看板缓存。 */
export async function refreshStoryboardVideoCollaborationCache(
  queryClient: QueryClient,
  payload: {
    storyboardId?: string | null;
    projectId?: string | null;
    parentProjectId?: string | null;
    targetProjectId?: string | null;
    affectedProjectIds?: readonly string[];
  }
): Promise<void> {
  const storyboardId = payload.storyboardId?.trim();
  const refreshTasks: Array<Promise<unknown>> = [];

  if (storyboardId) {
    refreshTasks.push(
      queryClient.refetchQueries({
        queryKey: ['storyboard-plans', 'storyboard', storyboardId, 2],
        type: 'all',
      }),
      queryClient.refetchQueries({
        queryKey: storyboardVideosKeys.list(storyboardId),
        type: 'all',
      })
    );
  }

  refreshTasks.push(
    refetchActiveStoryboardBoardQueries(
      queryClient,
      collectStoryboardVideoRefreshProjectIds(payload)
    )
  );

  await Promise.all(refreshTasks);
}

export function removeDeletedStoryboardVideoFromCache(
  queryClient: QueryClient,
  params: {
    storyboardId: string;
    videoId: string;
  }
): void {
  const { storyboardId, videoId } = params;
  const listKey = storyboardVideosKeys.list(storyboardId);

  queryClient.setQueryData<StoryboardVideoResponse[]>(listKey, (current = []) =>
    current.filter((item) => item.videoId !== videoId)
  );

  useCollaborationHighlightStore.getState().clearHighlight({
    targetType: 'storyboard_video',
    targetId: videoId,
  });
}

export function applyStoryboardVideoDeletedCacheSync(
  queryClient: QueryClient,
  payload: {
    storyboardId?: string | null;
    entityId?: string | null;
  }
): void {
  const storyboardId = payload.storyboardId?.trim();
  const videoId = payload.entityId?.trim();
  if (!storyboardId || !videoId) return;

  removeDeletedStoryboardVideoFromCache(queryClient, { storyboardId, videoId });
}
